import React, { useState } from 'react';
import {
    Box,
    Button,
    Typography,
    Paper,
    Avatar,
    Stack,
    Divider,
    useTheme,
    IconButton,
    Tooltip,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

function UserProfile({ compact = false }) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { currentUser, userData, logout } = useAuth();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);

    const handleLogout = async () => {
        setIsLoggingOut(true);
        try {
            await logout();
        } catch (error) {
            console.error('Błąd podczas wylogowania:', error);
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSettingsClick = () => {
        handleMenuClose();
        navigate('/settings');
    };

    if (!currentUser) {
        return null;
    }

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const displayName = userData?.display_name || currentUser.displayName || currentUser.email?.split('@')[0] || 'Użytkownik';
    const userEmail = currentUser.email;

    if (compact) {
        return (
            <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title={`${displayName} (${userData?.role || 'user'})`}>
                    <IconButton
                        onClick={handleMenuOpen}
                        size="small"
                        sx={{
                            p: 0.5,
                            '&:hover': {
                                transform: 'scale(1.05)'
                            }
                        }}
                    >
                        <Avatar
                            src={currentUser.photoURL}
                            sx={{
                                width: 32,
                                height: 32,
                                backgroundColor: theme.palette.primary.main
                            }}
                        >
                            {!currentUser.photoURL && getInitials(displayName)}
                        </Avatar>
                    </IconButton>
                </Tooltip>

                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: 'white',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '120px'
                    }}
                >
                    {displayName}
                </Typography>

                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'rgba(17, 24, 39, 0.95)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(76, 29, 149, 0.3)',
                            borderRadius: '16px',
                            mt: 1,
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
                        }
                    }}
                >
                    <MenuItem
                        onClick={handleSettingsClick}
                        sx={{
                            color: 'white',
                            borderRadius: '8px',
                            mx: 1,
                            my: 0.5,
                            '&:hover': {
                                backgroundColor: 'rgba(76, 29, 149, 0.3)'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <SettingsIcon sx={{ color: 'rgb(196, 181, 253)' }} />
                        </ListItemIcon>
                        <ListItemText primary="Ustawienia" />
                    </MenuItem>
                    <MenuItem
                        onClick={handleLogout}
                        disabled={isLoggingOut}
                        sx={{
                            color: 'white',
                            borderRadius: '8px',
                            mx: 1,
                            my: 0.5,
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.3)'
                            }
                        }}
                    >
                        <ListItemIcon>
                            <LogoutIcon sx={{ color: 'rgb(196, 181, 253)' }} />
                        </ListItemIcon>
                        <ListItemText primary={isLoggingOut ? "Wylogowywanie..." : "Wyloguj się"} />
                    </MenuItem>
                </Menu>
            </Stack>
        );
    }

    return (
        <Paper
            elevation={1}
            sx={{
                p: 2,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center">
                <Avatar
                    src={currentUser.photoURL}
                    sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: theme.palette.primary.main
                    }}
                >
                    {!currentUser.photoURL && getInitials(displayName)}
                </Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {displayName}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {userEmail}
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                    >
                        Rola: {userData?.role || 'user'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                    <Tooltip title="Ustawienia">
                        <IconButton
                            onClick={handleSettingsClick}
                            size="small"
                            sx={{
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.primary.main,
                                    backgroundColor: theme.palette.primary.main + '20'
                                }
                            }}
                        >
                            <SettingsIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title="Wyloguj się">
                        <IconButton
                            onClick={handleLogout}
                            disabled={isLoggingOut}
                            size="small"
                            sx={{
                                color: theme.palette.text.secondary,
                                '&:hover': {
                                    color: theme.palette.error.main,
                                    backgroundColor: theme.palette.error.main + '20'
                                }
                            }}
                        >
                            <LogoutIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Stack>
        </Paper>
    );
}

export default UserProfile;
